import { BottomNavigation, Button, Tabs, useForm } from '@otp/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentWrapper } from 'src/components/common/wrapper/content/wrapper';
import { UserGroupDetailsItem, UserGroupForm as Form } from './types';
import { CardWrapper } from 'src/components/common/wrapper/card/wrapper';
import { UserGroupForm } from './form/form';
import {
  CreateUserGroupDocument,
  UpdateUserGroupDocument,
} from 'src/graphql/generated/graphql';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Routes } from 'src/components/common/router/constants';
import { PermissionsFormList } from 'src/components/common/form/permissions';
import { UsersFormList } from 'src/components/common/form/users';
import { RolesFormList } from 'src/components/common/form/roles';

enum TabType {
  Users = 'users',
  Roles = 'roles',
  Permissions = 'permissions',
}

const REFETCH_QUERIES = ['UserGroupsDocument'];

export const UserGroupItem = ({
  userGroup = {},
}: {
  userGroup?: Partial<UserGroupDetailsItem>;
}) => {
  const [activeTab, setActiveTab] = useState(TabType.Users);

  const [selectedUsers, setSelectedUsers] = useState(userGroup.users || []);
  const [selectedRoles, setSelectedRoles] = useState(userGroup.roles || []);
  const [selectedPermissions, setSelectedPermissions] = useState(
    userGroup.permissions || [],
  );

  const navigate = useNavigate();
  const { t } = useTranslation();

  const [createUserGroup, { loading: loadingCreate }] = useMutation(
    CreateUserGroupDocument,
  );
  const [updateUserGroup, { loading: loadingUpdate }] = useMutation(
    UpdateUserGroupDocument,
  );
  const loading = loadingCreate || loadingUpdate;

  const onSubmit = async (formData: Form) => {
    const data = {
      ...formData,
      userIds: selectedUsers.map((user) => user.id),
      roleIds: selectedRoles.map((role) => role.id),
      permissionIds: selectedPermissions.map((permission) => permission.id),
    };

    if (userGroup?.id) {
      await updateUserGroup({
        variables: {
          id: userGroup.id,
          data,
        },
        refetchQueries: REFETCH_QUERIES,
      });
    } else {
      await createUserGroup({
        variables: {
          data,
        },
        refetchQueries: REFETCH_QUERIES,
      });
    }

    onClose();
  };

  const onClose = () => {
    navigate(Routes.userGroups.path);
  };

  const tabs = [
    { label: t('common.users'), value: TabType.Users },
    { label: t('common.roles'), value: TabType.Roles },
    { label: t('common.permissions'), value: TabType.Permissions },
  ];

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Form>({
    defaultValues: {
      name: userGroup.name,
      description: userGroup.description || '',
      isDisabled: userGroup.isDisabled || undefined,
      tagIds: userGroup.tags?.map((tag) => tag.id) || [],
      roleIds: userGroup.roles?.map((role) => role.id) || [],
      permissionIds:
        userGroup.permissions?.map((permission) => permission.id) || [],
      userIds: userGroup.users?.map((user) => user.id) || [],
    },
  });

  return (
    <ContentWrapper>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardWrapper>
          <UserGroupForm
            control={control}
            errors={errors}
            hiddenFields={userGroup?.id ? [] : ['isDisabled']}
          />
        </CardWrapper>
        <CardWrapper className="mt-6">
          <Tabs
            className="mb-4"
            tabs={tabs}
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
          />
          {activeTab === TabType.Users && (
            <UsersFormList
              selectedUsers={selectedUsers}
              onChange={setSelectedUsers}
            />
          )}
          {activeTab === TabType.Roles && (
            <RolesFormList
              selectedRoles={selectedRoles}
              onChange={setSelectedRoles}
            />
          )}
          {activeTab === TabType.Permissions && (
            <PermissionsFormList
              selectedPermissions={selectedPermissions}
              onChange={setSelectedPermissions}
            />
          )}
        </CardWrapper>
        <BottomNavigation>
          <Button
            className="mr-2"
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{ minWidth: 128 }}
          >
            {t('common.form.buttons.save')}
          </Button>
          <Button className="mr-4" variant="outlined" onClick={onClose}>
            {t('common.form.buttons.cancel')}
          </Button>
        </BottomNavigation>
      </form>
    </ContentWrapper>
  );
};
